import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Warning, CheckCircle, Broadcast } from "@phosphor-icons/react";

const barColor = (pct, threshold) =>
  pct >= 95 ? "bg-red-500" : pct >= threshold ? "bg-amber-500" : pct >= 60 ? "bg-yellow-400" : "bg-emerald-500";

function LoadBar({ pct, threshold }) {
  return (
    <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
      <div className={`h-full rounded-full transition-all ${barColor(pct, threshold)}`}
        style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
}

export default function CapacityOverview() {
  const [data, setData] = useState({ nodes: [], breakdown: [], threshold_pct: 80 });
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const settled = await Promise.allSettled([
        api.get("/servers/capacity-overview"),
        api.get("/alerts", { params: { unacknowledged: true } }),
      ]);
      if (settled[0].status === "fulfilled") setData(settled[0].value.data || { nodes: [], breakdown: [], threshold_pct: 80 });
      if (settled[1].status === "fulfilled") setAlerts(settled[1].value.data || []);
      else setAlerts([]);
      // Only toast if capacity overview itself failed (alerts are optional).
      if (settled[0].status === "rejected") {
        toast.error(formatApiError(settled[0].reason?.response?.data?.detail, settled[0].reason));
      }
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const ackAll = async () => {
    try { await api.post("/alerts/ack-all"); toast.success("Alerts acknowledged"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const ackOne = async (id) => {
    try { await api.post(`/alerts/${id}/ack`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const threshold = data.threshold_pct || 80;
  const activeNodes = useMemo(() => data.nodes
    .filter((n) => n.status === "active" || n.auto_disabled || n.health === "down")
    .sort((a, b) => b.utilization_pct - a.utilization_pct),
    [data.nodes]);
  const NODE_CAP = 12;
  const shownNodes = activeNodes.slice(0, NODE_CAP);

  if (loading) return null;

  return (
    <div className="mb-8 space-y-5" data-testid="capacity-overview">
      {/* Overload alerts banner */}
      {alerts.length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4" data-testid="capacity-alerts-banner">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-amber-600 font-semibold text-sm">
              <Warning weight="fill" className="w-5 h-5" /> {alerts.length} capacity alert{alerts.length > 1 ? "s" : ""}
            </div>
            <Button size="sm" variant="outline" className="rounded-lg" onClick={ackAll} data-testid="ack-all-alerts">
              <CheckCircle className="w-4 h-4 mr-1.5" /> Acknowledge all
            </Button>
          </div>
          <div className="space-y-1.5">
            {alerts.slice(0, 6).map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm" data-testid={`alert-${a.id}`}>
                <span><span className="font-medium">{a.customer_name}</span> — {a.message}
                  {a.emailed && <span className="ml-2 text-xs text-muted-foreground">· emailed</span>}</span>
                <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => ackOne(a.id)} data-testid={`ack-alert-${a.id}`}>Dismiss</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-node load */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Broadcast weight="fill" className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Live capacity — {activeNodes.length} active node{activeNodes.length !== 1 ? "s" : ""}</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="capacity-node-grid">
          {shownNodes.map((n) => (
            <div key={n.id} className="rounded-xl border border-border bg-card card-shadow p-4" data-testid={`cap-node-${n.id}`}>
              <div className="flex items-start justify-between">
                <div><p className="font-medium text-sm">{n.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{n.host}</p></div>
                <span className={`text-sm font-semibold tabular-nums ${n.utilization_pct >= threshold ? "text-amber-600" : ""}`}
                  data-testid={`cap-node-pct-${n.id}`}>{n.utilization_pct}%</span>
              </div>
              <div className="mt-2 flex items-center gap-1.5" data-testid={`cap-node-health-${n.id}`}>
                <span className={`inline-block w-2 h-2 rounded-full ${n.health === "up" ? "bg-emerald-500" : n.health === "down" ? "bg-red-500" : "bg-muted-foreground/40"}`} />
                <span className="text-xs text-muted-foreground">
                  {n.health === "up" ? "Healthy" : n.health === "down" ? "SIP down" : "Health unknown"}
                </span>
                {n.auto_disabled && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-500/15 text-red-600">Auto-disabled</span>}
                {!n.health_check_enabled && <span className="text-[10px] text-muted-foreground/70">(checks off)</span>}
              </div>
              <div className="mt-3"><LoadBar pct={n.utilization_pct} threshold={threshold} /></div>
              <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                <span className="tabular-nums">{n.active_calls} / {n.capacity ?? "∞"} calls</span>
                <span>{n.assigned_customers} customer{n.assigned_customers !== 1 ? "s" : ""}</span>
              </div>
            </div>
          ))}
          {activeNodes.length === 0 && <p className="text-sm text-muted-foreground">No active media nodes match.</p>}
        </div>
        {activeNodes.length > NODE_CAP && (
          <p className="text-xs text-muted-foreground mt-2" data-testid="cap-node-more">
            Showing top {NODE_CAP} of {activeNodes.length} by utilization — filter by node name/host to narrow down.
          </p>
        )}
      </div>
    </div>
  );
}
